import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

interface LogEntry {
  id: string;
  timestamp: string;
  message: string;
  data?: any;
}

interface LoggingContextType {
  logs: LogEntry[];
  addLog: (message: string, data?: any) => void;
  getLogs: () => LogEntry[];
  clearLogs: () => void;
  getLogCount: () => number;
}

const LoggingContext = createContext<LoggingContextType | undefined>(undefined);

export const useLogging = () => {
  const context = useContext(LoggingContext);
  if (context === undefined) {
    throw new Error('useLogging must be used within a LoggingProvider');
  }
  return context;
};

interface LoggingProviderProps {
  children: ReactNode;
}

export const LoggingProvider: React.FC<LoggingProviderProps> = ({ children }) => {
  // Initialize logs from localStorage or empty array
  const [logs, setLogs] = useState<LogEntry[]>(() => {
    try {
      const savedLogs = localStorage.getItem('auditio-upload-logs');
      const parsedLogs = savedLogs ? JSON.parse(savedLogs) : [];
      console.log('🔗 LoggingProvider: Initialized with', parsedLogs.length, 'logs from localStorage');
      return parsedLogs;
    } catch (error) {
      console.warn('Failed to load logs from localStorage:', error);
      return [];
    }
  });
  const maxLogs = 500; // Keep last 500 logs

  // Debug: Log when provider is recreated
  useEffect(() => {
    console.log('🔗 LoggingProvider: Component mounted/recreated with', logs.length, 'logs');
  }, []);

  const addLog = (message: string, data?: any) => {
    const newLog: LogEntry = {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      timestamp: new Date().toISOString(),
      message,
      data
    };

    setLogs(prevLogs => {
      const updatedLogs = [newLog, ...prevLogs];
      // Keep only the last maxLogs entries
      const trimmedLogs = updatedLogs.slice(0, maxLogs);
      
      // Save to localStorage
      try {
        localStorage.setItem('auditio-upload-logs', JSON.stringify(trimmedLogs));
        console.log('🔗 LoggingProvider: Saved', trimmedLogs.length, 'logs to localStorage');
      } catch (error) {
        console.warn('Failed to save logs to localStorage:', error);
      }
      
      return trimmedLogs;
    });

    // Also log to console for immediate visibility
    console.log(`📝 ${message}`, data || '');
  };

  const getLogs = () => {
    return [...logs];
  };

  const clearLogs = () => {
    setLogs([]);
    // Clear from localStorage
    try {
      localStorage.removeItem('auditio-upload-logs');
    } catch (error) {
      console.warn('Failed to clear logs from localStorage:', error);
    }
  };

  const getLogCount = () => {
    return logs.length;
  };

  const value: LoggingContextType = {
    logs,
    addLog,
    getLogs,
    clearLogs,
    getLogCount
  };

  return (
    <LoggingContext.Provider value={value}>
      {children}
    </LoggingContext.Provider>
  );
}; 